import { useOtChainState } from '@react/redux/store'

import { Card, CardContent, Typography } from '@mui/material'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { PoolBaseConfigurationCTO, PoolEventCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

import { fetchPools } from '@react/fetchers/PoolFetchers'
import { PoolTypeEnum } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { EventTypeForTemplate } from '@shared/templateApx'

interface TotalDepositsAllPoolsProps {
  poolAddr?: string
  lenderAddr: string
  currency: string
  lengthDays: number
  yearToDate?: boolean
  allTime?: boolean
  callback: (newTotal: number) => void
}

export const TotalDepositsAllPools: React.FC<TotalDepositsAllPoolsProps> = ({
  poolAddr,
  lenderAddr,
  currency,
  lengthDays,
  yearToDate,
  allTime,
  callback,
}) => {
  const {
    stageConfiguration: { graphqlUrl, restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const [totalDeposits, setTotalDeposits] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const lengthDaysAgoTimestamp: number = new Date(Date.now() - lengthDays * 24 * 60 * 60 * 1000).getTime()

  useEffect(() => {
    async function fetchDeposits(): Promise<void> {
      if (!providerReadOnly) return
      setIsLoading(true)
      setTotalDeposits(0)
      let localTotalDeposits = 0

      if (poolAddr === 'All Vaults') {
        const allPools: PoolBaseConfigurationCTO[] = await fetchPools(
          serverApiBaseUrl,
          restAPIKey,
          chainConfigurationName
        )
        const filteredPools: PoolBaseConfigurationCTO[] = allPools.filter((pool) => {
          return pool.liquidityTokenSymbol === currency
        })
        for (const pool of filteredPools) {
          const events = await getEvents(pool.poolAddr, lenderAddr)
          if (pool.poolType === PoolTypeEnum.FlexibleRate) {
            const depositEvents: PoolEventCTO[] = events.filter((e) => e.eventType === EventTypeForTemplate.PoolDeposit)
            const filteredDepositEvents: PoolEventCTO[] = depositEvents.filter(
              (e) => e.timestamp > lengthDaysAgoTimestamp / 1000
            )
            filteredDepositEvents.forEach((deposit) => {
              localTotalDeposits += Number(deposit.assets)
            })
          } else if (pool.poolType === PoolTypeEnum.Term) {
            const depositEvents: PoolEventCTO[] = events.filter((e) => e.eventType === EventTypeForTemplate.Deposit)
            const filteredDepositEvents: PoolEventCTO[] = depositEvents.filter(
              (e) => e.timestamp > lengthDaysAgoTimestamp / 1000
            )
            filteredDepositEvents.forEach((deposit) => {
              localTotalDeposits += Number(deposit.assets)
            })
          }
        }
        setTotalDeposits(localTotalDeposits)
        callback(localTotalDeposits)
      } else {
        if (!poolAddr) {
          logOT.error('Pool address is undefined')
          return
        }
        let localTotalDeposits = 0
        const allPools: PoolBaseConfigurationCTO[] = await fetchPools(
          serverApiBaseUrl,
          restAPIKey,
          chainConfigurationName
        )
        const pool = allPools.find((p) => p.poolAddr === poolAddr)
        if (!pool) {
          logOT.error('Pool not found')
          return
        }
        const events = await getEvents(poolAddr, lenderAddr)
        if (pool.poolType === PoolTypeEnum.FlexibleRate) {
          const depositEvents: PoolEventCTO[] = events.filter((e) => e.eventType === EventTypeForTemplate.PoolDeposit)
          const filteredDepositEvents: PoolEventCTO[] = depositEvents.filter(
            (e) => e.timestamp > lengthDaysAgoTimestamp / 1000
          )
          filteredDepositEvents.forEach((deposit) => {
            localTotalDeposits += Number(deposit.assets)
          })
        } else if (pool.poolType === PoolTypeEnum.Term) {
          const depositEvents: PoolEventCTO[] = events.filter((e) => e.eventType === EventTypeForTemplate.Deposit)
          const filteredDepositEvents: PoolEventCTO[] = depositEvents.filter(
            (e) => e.timestamp > lengthDaysAgoTimestamp / 1000
          )
          filteredDepositEvents.forEach((deposit) => {
            localTotalDeposits += Number(deposit.assets)
          })
        }
        setTotalDeposits(localTotalDeposits)
        callback(localTotalDeposits)
      }
      setIsLoading(false)
    }
    void fetchDeposits()
  }, [restAPIKey, providerReadOnly, lenderAddr, currency, poolAddr])

  const getEvents = async (poolAddr: string, accountAddr: string): Promise<PoolEventCTO[]> => {
    let query = ''
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
      const axios = require('axios')
      const propertyListString = ['id', 'poolAddr', 'poolName', 'eventType', 'assets', 'timestamp'].join('\n')
      if (accountAddr === 'All Lenders') {
        query = `query GetPoolEvents($poolAddr: String!) {
          getPoolEvents(
              poolAddr: $poolAddr
          ) {
              ${propertyListString}
          }
        }`
      } else {
        query = `query GetPoolEvents($poolAddr: String!, $accountAddr: String!) {
          getPoolEvents(
              poolAddr: $poolAddr
              accountAddr: $accountAddr
          ) {
              ${propertyListString}
          }
        }`
      }

      const variables = {
        poolAddr,
        accountAddr,
      }

      const response = await axios.post(
        `${graphqlUrl}/graphql`,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': restAPIKey,
          },
        }
      )
      logOT.debug('#getPoolEvents response', response.data.data.getPoolEvents)
      return response.data.data.getPoolEvents
    } catch (e) {
      logOT.error('fetchPoolOverviewCTOs error', e)
      return []
    }
  }

  return (
    <Card>
      <CardContent>
        {!yearToDate && !allTime && (
          <Typography sx={{ color: '#666' }} variant="h6">
            Total Deposits - <span style={{ fontWeight: 'bold', color: '#333' }}>{lengthDays} Days</span>
          </Typography>
        )}
        {yearToDate && (
          <Typography sx={{ color: '#666' }} variant="h6">
            Total Deposits - <span style={{ fontWeight: 'bold', color: '#333' }}>Year-to-date</span>
          </Typography>
        )}
        {allTime && (
          <Typography sx={{ color: '#666' }} variant="h6">
            Total Deposits - <span style={{ fontWeight: 'bold', color: '#333' }}>All-time</span>
          </Typography>
        )}
        {isLoading && (
          <Typography sx={{ fontWeight: 'normal', fontSize: '1em' }} variant="body2">
            Loading...
          </Typography>
        )}
        {!isLoading && (
          <Typography sx={{ fontWeight: 'normal', fontSize: '1.5em' }} variant="body2">
            {erc20WeiToCommaDecString(totalDeposits)} {currency}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
